import { isEqual, last } from 'lodash-es';
import { debounce } from 'lodash-es';
import {
  type PropsWithChildren,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useCustomEventEmitter } from '@/hook';

import { DEFAULT_ITEM_LIST_HEIGHT, ITEM_HEIGHT } from './constant';
import VBaseSelectContext from './context';
import type {
  VBaseSelectContextValue,
  VBaseSelectEventMap,
  VBaseSelectProps,
} from './type';

const VBaseSelectContextProvider = <M extends boolean = false>({
  children,
  itemList: _itemList = [],
  itemListClassName,
  itemListHeight = DEFAULT_ITEM_LIST_HEIGHT,
  itemListVisible,
  fitContent,
  multiple,
  placeholder,
  readOnly,
  value,
  renderItem,
  onChange,
  onItemClick,
  onItemListClose,
}: PropsWithChildren<
  VBaseSelectProps<M> & { itemListVisible: boolean } & Pick<
      VBaseSelectEventMap,
      'onItemListClose'
    >
>) => {
  const { eventEmitter } = useCustomEventEmitter({
    onChange: onChange ?? (() => {}),
    onItemClick: onItemClick ?? (() => {}),
    onItemListClose: onItemListClose ?? (() => {}),
  });
  const [innerValue, setInnerValue] = useState(
    () =>
      (value != null ? value : !multiple ? null : []) as M extends true
        ? any[]
        : any,
  );
  const itemList = useMemo(
    () =>
      _itemList.map(({ label, value, disabled }) => ({
        label: label ?? value,
        value,
        disabled,
      })),
    [_itemList],
  );
  const displayedLabel = useMemo(() => {
    if (!multiple)
      return itemList.find(item => isEqual(item.value, innerValue))?.label;

    const _innerValue = (innerValue as any[]) ?? [];
    const labelList = itemList
      .filter(({ value }) => _innerValue.findIndex(v => isEqual(v, value)) >= 0)
      .map(i => i.label);

    return labelList.join(', ');
  }, [innerValue, itemList, multiple]);

  const isSelected: VBaseSelectContextValue['isSelected'] = itemValue =>
    !multiple
      ? isEqual(itemValue, innerValue)
      : innerValue?.findIndex((v: any) => isEqual(v, itemValue)) >= 0;

  const moveScrollToSelectedItem = debounce(() => {
    if (!$itemList.current) return;

    const $virtualScroll = $itemList.current.querySelector<HTMLDivElement>(
      '[data-testid="v-virtual-scroll-container"]',
    );

    if (!$virtualScroll) return;

    const targetValue = !multiple ? innerValue : last(innerValue ?? []);
    const index =
      itemList?.findIndex(item => isEqual(item.value, targetValue)) ?? -1;

    if (index < 0) return;

    $virtualScroll.scrollTo({
      top:
        index * ITEM_HEIGHT -
        ($itemList.current.offsetHeight >> 1) +
        (ITEM_HEIGHT >> 1),
    });
  }, 0);

  const select: VBaseSelectContextValue['select'] = newValue => {
    setInnerValue(prev => {
      if (!multiple) return newValue;

      const index = prev?.findIndex((v: any) => isEqual(v, newValue)) ?? -1;

      return index < 0
        ? [...(prev ?? []), newValue]
        : prev?.filter((v: any) => !isEqual(v, newValue));
    });
  };

  const $displayValue = useRef<Nullish<HTMLInputElement>>(null);
  const $itemList = useRef<Nullish<HTMLDivElement>>(null);

  useEffect(() => {
    if (isEqual(value, innerValue)) return;

    eventEmitter.current.emit('change', {
      value: multiple ? [...innerValue] : innerValue,
    });

    if (multiple) return;

    eventEmitter.current.emit('itemListClose', null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [innerValue]);

  useEffect(() => {
    if (isEqual(value, innerValue)) return;

    setInnerValue(value as any);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    if (!itemListVisible) return;

    moveScrollToSelectedItem();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemListVisible, innerValue]);

  return (
    <VBaseSelectContext.Provider
      value={{
        displayedLabel,
        eventEmitter: eventEmitter.current,
        itemList,
        itemListClassName,
        itemListHeight,
        itemListVisible,
        fitContent,
        multiple,
        placeholder,
        readOnly,
        value: innerValue,

        isSelected,
        renderItem,
        select,

        $displayValue,
        $itemList,
      }}
    >
      {children}
    </VBaseSelectContext.Provider>
  );
};

export default VBaseSelectContextProvider;
